import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Headphones, PlayCircle } from "lucide-react";
import { siteInfo, aboutSummary, aboutImages, albums, gameProjects } from "../data/mock";
import AlbumCard from "../components/AlbumCard";
import CreditsMarquee from "../components/CreditsMarquee";
import GameAudioReel from "../components/GameAudioReel";
import SoundDesignShowcase from "../components/SoundDesignShowcase";
import { usePlayer } from "../context/PlayerContext";
import { Reveal } from "../hooks/useReveal";

const Home = () => {
  const { play } = usePlayer();
  // Featured project: UG (game)
  const ugProject = gameProjects.find((p) => p.title === "UG");
  const featuredAlbum =
    albums.find((a) => a.title === "UG") ||
    [...albums].sort((a, b) => b.year.localeCompare(a.year))[0];
  const gameAlbums = albums.filter((a) => a.category === "games" && a.title !== "Cactus Jam");
  const otherAlbums = albums.filter((a) => a.category !== "games");
  const featured = otherAlbums.slice(0, 6);

  // Hero quick-play strip: selected playable posters
  const heroQuickPlay = [
    ...albums.filter((a) => ["35sv6h4QXM5p2RpUFPF6zu", "1vZcCwZK9u6mgDJX97YwN8"].includes(a.id)).map((a) => ({ ...a, poster: a.cover })),
    gameProjects.find((p) => p.title === "UG"),
    {
      title: "McGeorge School of Law",
      subtitle: "Original Motion Picture Soundtrack",
      label: "Documentary",
      id: "0Qv6IrZo0bPYVpKEwRHAYC",
      spotifyType: "album",
      cover: "https://i.scdn.co/image/ab67616d00001e029b0160d100351fa0193bc2d2",
      poster: "https://i.scdn.co/image/ab67616d00001e029b0160d100351fa0193bc2d2",
      tracks: 5,
      year: "2026",
    },
    gameProjects.find((p) => p.title === "Monkey Doo"),
    gameProjects.find((p) => p.title === "Angel Earth Edition"),
  ].filter(Boolean);

  return (
    <div className="bg-[#f4f1ec] text-black">
      {/* HERO */}
      <section className="relative overflow-hidden pt-24 md:pt-28 pb-8 md:pb-10">
        {/* Subtle accent glow */}
        <div
          className="absolute inset-0 pointer-events-none opacity-70"
          style={{
            background:
              "radial-gradient(45% 50% at 18% 25%, rgba(255,176,64,0.14), transparent 60%), radial-gradient(40% 60% at 82% 75%, rgba(50,180,170,0.12), transparent 60%)",
          }}
        />
        <div className="max-w-[1400px] mx-auto w-full px-6 lg:px-12 relative">
          <div className="relative grid grid-cols-12 items-center gap-4">
            <span className="hidden md:block col-span-2 text-sm tracking-[0.05em] text-neutral-700">
              Composer
            </span>
            <h1
              className="hero-name col-span-12 md:col-span-8 text-center text-[42px] sm:text-[58px] md:text-[80px] lg:text-[100px] leading-[1.02] tracking-[-0.02em] font-semibold"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              {siteInfo.name}
            </h1>
            <span className="hidden md:block col-span-2 text-right text-sm tracking-[0.05em] text-neutral-700">
              Musician
            </span>
            <div className="md:hidden col-span-12 flex justify-between text-xs uppercase tracking-[0.18em] text-neutral-600 px-2 mt-1">
              <span>Composer</span>
              <span>Musician</span>
            </div>
          </div>

          {/* Tagline */}
          <p
            className="mt-3 md:mt-4 text-center text-lg md:text-2xl lg:text-3xl italic font-light text-neutral-700 tracking-tight max-w-3xl mx-auto"
            style={{ fontFamily: "'Instrument Serif', serif" }}
          >
            Cinematic music for video games, VR &amp; film.
          </p>

          {/* Quick actions */}
          <div className="mt-6 md:mt-8 flex flex-col items-center gap-4">
            <button
              onClick={() => play(ugProject || featuredAlbum)}
              className="group inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white border border-black/15 text-black shadow-sm hover:border-black hover:shadow-md transition-all"
            >
              <span className="relative flex w-2.5 h-2.5">
                <span className="absolute inset-0 rounded-full bg-amber-500 animate-ping opacity-75" />
                <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-amber-500" />
              </span>
              <PlayCircle className="w-5 h-5" />
              <span className="text-sm tracking-tight">
                <span className="text-neutral-500">Now playing &middot; </span>
                <span className="font-medium">UG &mdash; Original Soundtrack</span>
              </span>
            </button>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 w-full sm:w-auto">
              <Link
                to="/music"
                className="group inline-flex items-center justify-center gap-3 px-10 sm:px-12 py-4 rounded-full bg-black text-white text-base font-medium tracking-tight w-full sm:w-auto min-w-[240px] transition-all duration-300 hover:bg-neutral-800 hover:scale-[1.02] shadow-[0_8px_30px_rgba(0,0,0,0.12)]"
              >
                <Headphones className="w-5 h-5" />
                Listen to Music
                <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/work"
                className="group inline-flex items-center justify-center gap-3 px-10 sm:px-12 py-4 rounded-full border-2 border-black text-black text-base font-medium tracking-tight w-full sm:w-auto min-w-[240px] transition-all duration-300 hover:bg-black hover:text-white hover:scale-[1.02]"
              >
                View Work
                <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>

          {/* Quick-play strip: instant access to game posters */}
          <div className="mt-8 md:mt-10">
            <div className="flex items-center justify-center gap-2 mb-3">
              <span className="text-[10px] uppercase tracking-[0.22em] text-neutral-500">
                Quick play
              </span>
              <span className="h-px w-10 bg-neutral-400/60" />
            </div>
            <div className="flex justify-center gap-3 md:gap-4 flex-wrap">
              {heroQuickPlay.map((p) => (
                <button
                  key={p.title}
                  onClick={() => play(p)}
                  className="group relative w-[88px] h-[110px] md:w-[110px] md:h-[140px] rounded-lg overflow-hidden shadow-lg hover:scale-[1.05] transition-transform focus:outline-none"
                  aria-label={`Play ${p.title}`}
                  title={p.title}
                >
                  <img
                    src={p.poster}
                    alt={p.title}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="w-8 h-8 rounded-full bg-amber-400 text-black flex items-center justify-center shadow-lg">
                      <PlayCircle className="w-5 h-5" />
                    </span>
                  </div>
                  <span className="absolute bottom-1.5 left-1.5 right-1.5 text-[10px] md:text-xs text-white font-medium truncate text-left drop-shadow">
                    {p.title}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <GameAudioReel />
      <SoundDesignShowcase />

      {/* GAMES SPOTLIGHT - UG featured */}
      <section className="py-12 md:py-16 relative overflow-hidden">
        {/* Game-themed warm gradient backdrop */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(60% 50% at 20% 30%, rgba(255,176,64,0.18), transparent 60%), radial-gradient(50% 50% at 80% 70%, rgba(50,180,170,0.16), transparent 60%)",
          }}
        />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative">
          <Reveal>
            <div className="flex items-end justify-between mb-10 md:mb-14 flex-wrap gap-4">
              <div>
                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-amber-700">
                  <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                  Now playing · Game Soundtracks
                </span>
                <h2
                  className="mt-3 text-4xl md:text-6xl lg:text-7xl tracking-tight font-semibold"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  {featuredAlbum.title}
                </h2>
                <p className="mt-2 text-neutral-700 text-lg">
                  {featuredAlbum.subtitle} · {featuredAlbum.type} ·{" "}
                  {featuredAlbum.tracks} tracks · {featuredAlbum.year}
                </p>
              </div>
              <Link
                to="/work/game-credits"
                className="inline-flex items-center gap-2 text-black font-medium border-b border-black pb-0.5 hover:gap-3 transition-all"
              >
                All game credits <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-stretch">
              <div className="lg:col-span-2">
                {/* Game poster (richer than album art) */}
                <button
                  onClick={() => play(ugProject || featuredAlbum)}
                  className="group block w-full text-left focus:outline-none"
                  aria-label="Play UG soundtrack"
                >
                  <div className="aspect-[4/5] overflow-hidden rounded-2xl shadow-2xl relative bg-neutral-900">
                    <img
                      src={ugProject?.poster || featuredAlbum.cover}
                      alt="UG"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                    <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.22em] text-white/90 bg-black/40 backdrop-blur px-2.5 py-1 rounded-full">
                      Meta Number 1
                    </span>
                    <div className="absolute right-5 bottom-5 w-16 h-16 rounded-full bg-amber-400 text-black flex items-center justify-center shadow-2xl transform translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                      <PlayCircle className="w-7 h-7" />
                    </div>
                  </div>
                </button>
              </div>
              <div
                className="lg:col-span-3 text-white rounded-2xl p-8 md:p-12 shadow-2xl relative overflow-hidden"
                style={{
                  background:
                    "linear-gradient(135deg, #1a1310 0%, #2b1d12 45%, #0e1f1f 100%)",
                }}
              >
                <div
                  className="absolute inset-0 opacity-40 blur-3xl"
                  style={{
                    backgroundImage: `url(${ugProject?.poster || featuredAlbum.cover})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-amber-400/20 blur-3xl" />
                <div className="absolute -left-20 -bottom-20 w-72 h-72 rounded-full bg-teal-400/15 blur-3xl" />
                <div className="relative">
                  <span className="text-xs uppercase tracking-[0.22em] text-amber-300/90">
                    Featured Soundtrack
                  </span>
                  <h3
                    className="mt-4 text-4xl md:text-6xl font-semibold tracking-tight"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    {featuredAlbum.title}
                  </h3>
                  <p
                    className="mt-3 text-white/80 text-lg md:text-xl max-w-md italic"
                    style={{ fontFamily: "'Instrument Serif', serif" }}
                  >
                    Cinematic adventure score across {featuredAlbum.tracks}{" "}
                    tracks &mdash; boss themes, atmospheres, and exploration cues.
                  </p>
                  <div className="mt-10 flex flex-wrap gap-3">
                    <button
                      onClick={() => play(ugProject || featuredAlbum)}
                      className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-amber-400 text-black text-sm font-semibold hover:bg-amber-300 hover:scale-[1.02] transition-all shadow-lg"
                    >
                      <PlayCircle className="w-5 h-5" /> Play album
                    </button>
                    <a
                      href={`https://open.spotify.com/album/${featuredAlbum.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/30 text-white text-sm font-medium hover:bg-white hover:text-black transition-colors"
                    >
                      Open in Spotify <ArrowUpRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Game albums quick row */}
          <Reveal delay={200}>
            <div className="mt-10 md:mt-14">
              <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                <h3
                  className="text-xl md:text-2xl font-medium tracking-tight"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  More game &amp; VR scores
                </h3>
                <Link
                  to="/work/game-credits"
                  className="text-sm text-neutral-700 hover:text-black"
                >
                  See all game credits →
                </Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-6">
                {gameAlbums
                  .filter((a) => a.id !== featuredAlbum.id)
                  .map((a, i) => (
                    <Reveal key={a.id} delay={i * 60}>
                      <AlbumCard album={a} size="sm" />
                    </Reveal>
                  ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PRESS / AS HEARD IN MARQUEE */}
      <CreditsMarquee />

      {/* FEATURED ALBUMS */}
      <section className="py-10 md:py-14">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <h2
                className="text-3xl md:text-5xl tracking-tight font-medium"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Film &amp; complete discography
              </h2>
              <Link
                to="/music"
                className="inline-flex items-center gap-2 text-black font-medium border-b border-black pb-0.5 hover:gap-3 transition-all"
              >
                Browse all <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5 md:gap-6">
            {featured.map((a, i) => (
              <Reveal key={a.id} delay={i * 60}>
                <AlbumCard album={a} size="sm" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="py-14 md:py-20">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
          <Reveal className="order-2 md:order-1">
            <span className="text-xs uppercase tracking-[0.22em] text-neutral-500">
              About
            </span>
            <h2
              className="mt-4 text-3xl md:text-5xl tracking-tight font-medium leading-tight"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Cinematic stories, told in sound.
            </h2>
            <p className="mt-6 text-neutral-700 text-base md:text-lg leading-relaxed">
              {aboutSummary}
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 mt-8 text-black font-medium border-b border-black pb-0.5 hover:gap-3 transition-all"
            >
              Read full bio <ArrowUpRight className="w-4 h-4" />
            </Link>
          </Reveal>
          <Reveal delay={120} className="order-1 md:order-2">
            <div className="aspect-[4/5] overflow-hidden rounded-md shadow-xl">
              <img
                src={aboutImages.studio}
                alt="Leonard in his studio"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
